import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Dialog, DialogActions, DialogContent, Grid, Icon, Stack, Typography } from '@mui/material'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { omit, template } from 'lodash'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import VerticalSortableList from 'components/draggable/VerticalSortableList'
import { DragHandle, SortableItem } from 'components/draggable/components/sortableItems/SortableItem'
import ConfirmModal from 'components/modal/ConfirmModal'
import useBoolean from 'utils/customHook/useBoolean'
import ApplicationConf from 'conf/ApplicationConf'
import { genericPromise2 } from 'utils/ActionUtils'
import OperationAction from 'quality/components/operation/actions/OperationAction'
import ToastrAction from 'toastr/actions/ToastrAction'
import WaitAction from 'wait/WaitAction'

// dénombrements de toutes les phases, des microprélèvements et des unités de relevé
const COUNT_KEYS = [
    'enumerationA', 'enumerationB', 'enumerationC', 'enumerationC2',
    'enumerationP1', 'enumerationP2', 'enumerationP3', 'enumerationP4', 'enumerationP5', 'enumerationP6',
    'enumerationP7', 'enumerationP8', 'enumerationP9', 'enumerationP10', 'enumerationP11', 'enumerationP12',
    'percentageA', 'percentageB', 'percentageC',
]

// un taxon est « non renseigné » quand tous ses dénombrements sont à 0 ou vides
const isEmptyHydrobioList = list => COUNT_KEYS.every(key => !list[key])

// exécute les promesses l'une après l'autre (évite de saturer le service et garde l'ordre des écritures)
const runSequentially = (items, promiseFn) => items.reduce((acc, item) => acc.then(() => promiseFn(item)), Promise.resolve())

// appels directs (sans les actions) : un seul toast pour toute l'opération, et une ligne en échec interrompt la suite
const updateList = (qualitometer, operation, id, list) => genericPromise2(ApplicationConf.qualitometer.hydrobioList(qualitometer, operation, id), { method: 'PUT', body: list })
    .then(res => (res?.update > 0 ? res : Promise.reject(new Error(`update ${id}`))))
const deleteList = (qualitometer, operation, id) => genericPromise2(ApplicationConf.qualitometer.hydrobioList(qualitometer, operation, id), { method: 'DELETE' })
    .then(res => (res?.delete > 0 ? res : Promise.reject(new Error(`delete ${id}`))))

const HydrobioListOrderDialog = ({
    isOpen = false,
    onClose = () => {},
    lists = [],
    getTaxonLabel = () => '',
}) => {
    const dispatch = useDispatch()

    const [items, setItems] = useState([])

    // à chaque ouverture on repart de l'ordre enregistré
    useEffect(() => {
        if (isOpen) {
            setItems(lists.map(list => ({ ...list, id: `${list.id}`, listId: list.id })))
        }
    }, [isOpen, lists])

    // L'ordre affiché est celui des codeliste : on garde les codeliste existants de l'opération
    // et on y réécrit le contenu des taxons dans le nouvel ordre (seules les lignes déplacées sont mises à jour).
    const onValidate = () => {
        const listIds = lists.map(list => list.id)
        const updates = items
            .map((item, index) => ({ item, targetId: listIds[index] }))
            .filter(({ item, targetId }) => item.listId !== targetId)
        if (!updates.length) {
            onClose()
            return
        }
        const { qualitometer, operation } = lists[0]
        dispatch(WaitAction.waitStart())
        runSequentially(updates, ({ item, targetId }) => updateList(qualitometer, operation, targetId, { ...omit(item, 'listId'), id: targetId }))
            .then(() => dispatch(ToastrAction.success(i18n.elementUpdateSuccess)))
            .catch(() => dispatch(ToastrAction.error(i18n.updateError + i18n.taxonsOrder)))
            .finally(() => {
                dispatch(OperationAction.fetchHydrobioList(qualitometer, operation))
                dispatch(WaitAction.waitStop())
                onClose()
            })
    }

    return (
        <Dialog
            maxWidth='md'
            fullWidth
            open={isOpen}
        >
            <DefaultDialogTitle
                title={i18n.taxonsOrder}
                onClose={onClose}
            />
            <DialogContent>
                <Typography variant='body2' color='text.secondary' sx={{ padding: '0.5rem 1rem 0' }}>
                    {i18n.taxonsOrderHelp}
                </Typography>
                <VerticalSortableList
                    items={items}
                    onChange={setItems}
                    renderItem={(item, index) => (
                        <SortableItem id={item.id}>
                            <Stack direction='row' spacing={1.5} alignItems='center'>
                                <DragHandle />
                                {Number.isInteger(index) ? `${index + 1} - ` : ''}{getTaxonLabel(item)}
                            </Stack>
                        </SortableItem>
                    )}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onValidate} variant='contained' color='primary'>
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

HydrobioListOrderDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    lists: PropTypes.arrayOf(PropTypes.object),
    getTaxonLabel: PropTypes.func,
}

// Boutons au-dessus des listes faunistiques / floristiques : ordre des taxons et suppression en masse des taxons sans dénombrement
const HydrobioListTools = ({
    getTaxonLabel = () => '',
}) => {
    const dispatch = useDispatch()

    const {
        hydrobioList,
        accountUser,
    } = useSelector(store => ({
        hydrobioList: store.OperationReducer.hydrobioList,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const {
        value: isOrderOpen,
        setFalse: onCloseOrder,
        setTrue: onOpenOrder,
    } = useBoolean(false)
    const {
        value: isDeleteOpen,
        setFalse: onCloseDelete,
        setTrue: onOpenDelete,
    } = useBoolean(false)

    const sortedLists = useMemo(() => [...hydrobioList].sort((a, b) => a.id - b.id), [hydrobioList])
    const emptyLists = useMemo(() => hydrobioList.filter(isEmptyHydrobioList), [hydrobioList])

    if (accountUser.consultant === '1' || !hydrobioList.length) {
        return null
    }

    const onDeleteEmpty = () => {
        const { qualitometer, operation } = emptyLists[0]
        dispatch(WaitAction.waitStart())
        runSequentially(emptyLists, list => deleteList(qualitometer, operation, list.id))
            .then(() => dispatch(ToastrAction.success(template(i18n.emptyTaxonsDeleted)({ nb: emptyLists.length }))))
            .catch(() => dispatch(ToastrAction.error(i18n.deleteEmptyTaxonsError)))
            .finally(() => {
                dispatch(OperationAction.fetchHydrobioList(qualitometer, operation))
                dispatch(WaitAction.waitStop())
                onCloseDelete()
            })
    }

    return (
        <>
            <Grid container spacing={1} justifyContent='flex-end' sx={{ padding: '0 10px 5px' }}>
                <Grid size='auto'>
                    <Button variant='outlined' onClick={onOpenOrder} startIcon={<Icon>swap_vert</Icon>}>
                        {i18n.taxonsOrder}
                    </Button>
                </Grid>
                <Grid size='auto'>
                    <Button variant='outlined' color='error' onClick={onOpenDelete} disabled={!emptyLists.length} startIcon={<Icon>delete_sweep</Icon>}>
                        {`${i18n.deleteEmptyTaxons} (${emptyLists.length})`}
                    </Button>
                </Grid>
            </Grid>
            <HydrobioListOrderDialog
                isOpen={isOrderOpen}
                onClose={onCloseOrder}
                lists={sortedLists}
                getTaxonLabel={getTaxonLabel}
            />
            <ConfirmModal
                isOpen={isDeleteOpen}
                title={i18n.deleteEmptyTaxons}
                subTitle={template(i18n.deleteEmptyTaxonsConfirm)({ nb: emptyLists.length })}
                onValidate={onDeleteEmpty}
                onClose={onCloseDelete}
            />
        </>
    )
}

HydrobioListTools.propTypes = {
    getTaxonLabel: PropTypes.func,
}

export {
    isEmptyHydrobioList,
}

export default HydrobioListTools
